/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'i18next';
import { confirmBox, getError, showPrice } from 'utils/appHelpers';
import { collectProductInfo, getProjectImgAndShareIcon } from 'utils/projectHelpers';
import { useAlert } from 'react-alert';
import { getLocalizedPath } from 'utils/languageHelpers';

// Components
import { AlarmIcon, ArrowLeftIcon, FolderIconFilled, PlusLargeIcon, TrashIconFilled } from 'shared/components/Icons';
import Button from 'shared/components/Button';
import ActionButtons from 'shared/components/likiee/ActionButtons';
import PriceAlertModal from 'shared/components/likiee/PriceAlertModal';
import SaveToProjectModal from 'shared/components/likiee/SaveToProjectModal';
import SizeAlertModal from 'shared/components/likiee/SizeAlertModal';
import { ProductImageBlock } from 'shared/components/likiee/ProductImageBlock';
import { removeProductModal, verifyEmailModal } from 'shared/utils/productHelpers';
import PriceChart from './components/PriceChart';
import PriceChartModal from './components/PriceChartModal';
import AICompare from 'shared/components/AICompare';

// Styles
import './ProductView.scss';
import {
  getAndUpdateProduct,
  onAddProduct,
  onUpdateProduct,
  subscribeToProductChanges,
  unSubscribeProductChanges,
} from '../../actions/products';
import { API, graphqlOperation } from 'aws-amplify';
import { getLikiee } from 'graphql/likiees/queries';
import { isEmailVerified } from 'app/actions/authActions';
import { sendEvent } from 'utils/analyticsHelpers';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import ProductInfoTabs from './components/ProductInfoTabs';
import ProductOtherOffers from './components/ProductOtherOffers';
import { VirtualTryOnImageWrapper } from 'shared/components/VirtualTryOn';

const ProductView = ({ match, history }) => {
  const dispatch = useDispatch();
  const account = useSelector(state => state.account);
  const products = useSelector(state => state.products);
  const alert = useAlert();
  const productId = match.params?.id;
  const [isOpenChart, setIsOpenChart] = useState(false);
  const [priceAlertModalState, setPriceAlertModalState] = useState(false);
  const [sizeAlertModalState, setSizeAlertModalState] = useState(false);
  const [projectModalState, setProjectModalState] = useState(false);
  const [saveToLink, setSaveToLink] = useState();
  const [isAICompareOpen, setIsAICompareOpen] = useState(false);
  const product = products[productId];
  const productProjects = product?.projects?.items || [];
  const productProjectsIDs = productProjects?.map(item => item.id) || [];
  const otherOffersRef = useRef(null);

  const scrollToOffers = () => {
    if (otherOffersRef.current) {
      otherOffersRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const goBack = () => {
    history.goBack ? history.goBack() : history.push(getLocalizedPath('/likiees/project/'));
  };

  const getProductAnalyticsParams = product => {
    return {
      id: product.id,
      source: product.sourceName,
      isAffiliate: !!product.affiliateURL,
      url: product.url, //TODO: check if we need to send this
    };
  };

  const getProduct = async () => {
    try {
      const body = { id: productId };
      const { data } = await API.graphql(graphqlOperation(getLikiee, body));
      if (data.getLikiee) {
        dispatch(onAddProduct(data.getLikiee));
        sendEvent('product_view', { ...getProductAnalyticsParams(data.getLikiee) });
      } else {
        alert.error(t('likiees_no_result'));
        history.push(getLocalizedPath('/likiees/project/'));
      }
    } catch (err) {
      alert.error(getError(err));
    }
  };

  const onAlertSuccess = updateData => {
    getAndUpdateProduct(product.id, updateData);
    setPriceAlertModalState(false);
    setSizeAlertModalState(false);
    const options = {
      title: t('alert_success_title'),
      message: t('alert_price_success_text'),
      confirm: t('btn_ok'),
    };
    confirmBox(options);
  };

  const onRemoveSuccess = () => {
    getAndUpdateProduct(product.id);
    setPriceAlertModalState(false);
    alert.success(t('msg_success_removed_price_alert'));
  };

  const onProductRemove = () => {
    removeProductModal(product, alert, t, () => {
      history.push(getLocalizedPath('/likiees/project/'));
    });
  };

  const onSetPriceAlertInsideChartModal = () => {
    setIsOpenChart(false);
    setTimeout(setPriceAlertModalState, 200, true);
  };

  const handlePriceAlert = () => {
    if (!isEmailVerified(account)) {
      verifyEmailModal(alert, t);
      return;
    }
    setPriceAlertModalState(true);
  };

  const handleOnSaveOfferProduct = url => {
    setSaveToLink(url);
  };

  const onUpdateProductSuccess = product => {
    dispatch(onUpdateProduct(product));
  };

  const handleAIButtonSuccess = () => {
    // Refresh product data to show updated AI summary
    getAndUpdateProduct(product.id);
  };

  useEffect(() => {
    getProduct();
  }, [productId]);

  useEffect(() => {
    subscribeToProductChanges({
      onUpdateProduct: onUpdateProductSuccess,
    });
    return () => {
      unSubscribeProductChanges();
    };
  }, []);

  if (!product) return <SkeletonLoader type='page' count={4} className='my-20' />;

  const { images, mainImage, favIcon, name, description, sourceName, properties } = collectProductInfo(product);
  const hasPriceAlert = !!product.priceAlert;

  const actionButtons = [
    { className: 'is-mob-l', logo: <TrashIconFilled />, onClick: onProductRemove },
    { className: 'is-mob-l', logo: <FolderIconFilled />, onClick: () => setProjectModalState(true) },
  ];

  return (
    <div className='product-view pb-15 position-relative'>
      {/* Decorative Elements */}
      <div className='decorative-circle decorative-circle--1' />
      <div className='decorative-circle decorative-circle--2' />

      {/* Modern Back Button */}
      <div className='position-absolute z-index-5 back-btn-modern'>
        <button className='btn btn-back-modern' onClick={goBack}>
          <ArrowLeftIcon />
          <span className='back-text'>{t('btn_back')}</span>
        </button>
      </div>

      {/* Main Product Section */}
      <div className='row position-relative' style={{ zIndex: 1 }}>
        <div className='col-12 col-md-6 order-md-1'>
          <VirtualTryOnImageWrapper likiee={product} productImage={mainImage}>
            <ProductImageBlock
              className={'rounded-10 border'}
              sliderClassName='product-view-slider arrows-initial'
              images={images}
              mainImage={mainImage}
              status={product.status}
            />
          </VirtualTryOnImageWrapper>
          <div className='mt-6 mt-md-10 mb-15 mb-md-20 d-flex align-items-center'>
            <p className='mr-10 mr-md-20 mb-0 text-sm text-md-lg'>{t('text_projects')}</p>
            <div className='project-images d-flex align-items-center'>
              {productProjects.map((item, i) => {
                const { image, isNoImage } = getProjectImgAndShareIcon(item);
                return (
                  <div key={i} className={`rounded-100p ${isNoImage ? 'no-image' : ''} mr-1 mr-md-2`}>
                    {!isNoImage && <img className='rounded-100p' src={image} alt='img' />}
                    {isNoImage && <FolderIconFilled />}
                  </div>
                );
              })}
              <button className='btn p-0 btn-circle' onClick={() => setProjectModalState(true)}>
                <PlusLargeIcon />
              </button>
            </div>
          </div>
        </div>

        <div className='col-12 col-md-6 position-relative order-md-2'>
          <ActionButtons buttons={actionButtons} className='horizontal-list right-corner' />
          <h3 className='mb-8 pr-15 mt-15 mt-md-0 product-title'>{name}</h3>
          <div className='mb-7'>
            <div className='price-display'>
              {product.status === 'EXTRACTING' ? (
                <div className='skeleton-text skeleton-price-field skeleton-pulse' />
              ) : (
                <>
                  <h1 className='mb-0'>{showPrice(product, 'price', 'range')}</h1>
                  {!product.price && product.priceRange && product.priceRange.count > 1 && (
                    <small className='text-muted d-block mt-1'>{product.priceRange.count} offers available</small>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Product Information Tabs - Redesigned Section */}
          <ProductInfoTabs
            sourceName={sourceName}
            favIcon={favIcon}
            product={product}
            description={description}
            properties={properties}
            isLoadingProduct={!product}
            aiSummary={product?.aiSummary}
            reviewsAggregation={product?.reviewsAggregation}
            onAIButtonSuccess={handleAIButtonSuccess}
            onAICompareClick={() => setIsAICompareOpen(true)}
          />

          {/* Action Buttons Section */}
          <div className='product-actions mt-8 mt-md-20'>
            <div className='actions-row d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-3'>
              {/* Primary Action Button */}
              <div className='primary-action'>
                {product.url ? (
                  <a
                    href={product.affiliateURL || product.url}
                    onClick={() => sendEvent('visit_product', { ...getProductAnalyticsParams(product) })}
                    target='_blank'
                    className='btn btn-primary btn-lg px-5 py-3 w-100'
                    rel='noreferrer'
                  >
                    {t('btn_visit_product')}
                  </a>
                ) : (
                  product.visualMatches &&
                  product.visualMatches.length > 0 && (
                    <button onClick={scrollToOffers} className='btn btn-primary btn-lg px-5 py-3 w-100'>
                      {t('btn_see_offers')}
                    </button>
                  )
                )}
              </div>

              {/* Secondary Action - Price Alert */}
              {product.url && (
                <div className='secondary-action'>
                  <Button
                    className='btn-outline-primary btn-outline-fixed py-3 px-4 btn-lg w-100 text-uppercase position-relative'
                    onClick={handlePriceAlert}
                  >
                    <AlarmIcon className='mr-3' />
                    {hasPriceAlert ? t('btn_edit_price_alarm') : t('btn_set_price_alarm')}
                    <span className='badge badge-premium position-absolute'>PRO</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Price Chart Section - Full Width */}
      <div className='row mt-12 mt-md-15 position-relative' style={{ zIndex: 1 }}>
        <div className='col-12'>
          <div onClick={() => setIsOpenChart(true)}>
            <PriceChart product={product} hideFilter={true} hideX={false} hideY={false} />
          </div>
        </div>
      </div>
      <div ref={otherOffersRef} className='position-relative' style={{ zIndex: 1 }}>
        <ProductOtherOffers product={product} data={product.visualMatches} onClickSave={handleOnSaveOfferProduct} />
      </div>
      {priceAlertModalState && (
        <PriceAlertModal
          product={product}
          onClose={() => setPriceAlertModalState(false)}
          onSuccess={onAlertSuccess}
          onRemoveSuccess={onRemoveSuccess}
        />
      )}
      {sizeAlertModalState && (
        <SizeAlertModal product={product} onClose={() => setSizeAlertModalState(false)} onSuccess={onAlertSuccess} />
      )}
      {(projectModalState || saveToLink) && (
        <SaveToProjectModal
          saveToLink={saveToLink}
          productID={product.id}
          initialProjectIDs={productProjectsIDs}
          onClose={() => {
            setSaveToLink();
            setProjectModalState(false);
          }}
        />
      )}
      {isOpenChart && (
        <PriceChartModal
          onClose={() => setIsOpenChart(false)}
          product={product}
          onSetPriceAlert={onSetPriceAlertInsideChartModal}
        />
      )}
      <AICompare isOpen={isAICompareOpen} onClose={() => setIsAICompareOpen(false)} currentProduct={product} />
    </div>
  );
};

export default ProductView;
